import { useEffect, useState, type ChangeEvent } from "react";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { usePageContext } from "@ryde/redux";
import {
  bulkGridSelectionRowCountEditingAF,
  parseBulkGridSelectionRowCountDraft,
} from "@ryde/shared";

export const useBulkGridSelectionRowCountInput = ({
  rowCount,
  setRowCount,
}: {
  rowCount: number;
  setRowCount: (value: number) => void;
}): {
  inputValue: string;
  hasPendingChanges: boolean;
  onFocus: () => void;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  onApply: () => void;
} => {
  const { entity } = usePageContext();
  const [draft, setDraft] = useState<string | null>(null);
  const setIsRowCountEditing = useSetRecoilState(
    bulkGridSelectionRowCountEditingAF(entity),
  );

  useEffect(() => {
    return () => {
      setIsRowCountEditing(false);
    };
  }, [setIsRowCountEditing]);

  const inputValue = draft ?? String(rowCount);

  const hasPendingChanges =
    draft !== null &&
    parseBulkGridSelectionRowCountDraft({ raw: draft, fallback: rowCount }) !==
      rowCount;

  const endEditing = useEvent((): void => {
    setIsRowCountEditing(false);
    setDraft(null);
  });

  const onApply = useEvent((): void => {
    if (draft === null) {
      return;
    }

    const next = parseBulkGridSelectionRowCountDraft({
      raw: draft,
      fallback: rowCount,
    });

    if (next !== rowCount) {
      setRowCount(next);
    }

    endEditing();
  });

  const onFocus = useEvent((): void => {
    setIsRowCountEditing(true);
    setDraft(String(rowCount));
  });

  const onChange = useEvent((event: ChangeEvent<HTMLInputElement>): void => {
    const next = event.target.value;

    if (next === "" || /^\d+$/.test(next)) {
      setDraft(next);
    }
  });

  const onBlur = useEvent((): void => {
    endEditing();
  });

  return {
    inputValue,
    hasPendingChanges,
    onFocus,
    onChange,
    onBlur,
    onApply,
  };
};
